import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useActiveGame } from '@/hooks/useShared';
import { ActiveGamePage } from '@/pages/ActiveGamePage';
import { SettlementPage } from '@/pages/SettlementPage';
import { LoadingSpinner, EmptyState, Button } from '@/components/ui';
import type { Game } from '@/lib/types';

export function GameScreen() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeGame, loading: activeLoading } = useActiveGame();
  const [trackedGameId, setTrackedGameId] = useState<string | null>(null);
  const [specificGame, setSpecificGame] = useState<Game | null>(null);
  const [specificLoading, setSpecificLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (gameId) {
      setTrackedGameId(gameId);
    } else if (!trackedGameId && activeGame) {
      setTrackedGameId(activeGame.id);
    }
  }, [gameId, activeGame, trackedGameId]);

  useEffect(() => {
    const targetId = gameId || trackedGameId;
    if (!targetId) {
      setSpecificGame(null);
      setSpecificLoading(false);
      setNotFound(false);
      return;
    }

    setSpecificLoading(true);
    const unsub = onSnapshot(doc(db, 'games', targetId), (snap) => {
      if (snap.exists()) {
        const gameData = snap.data() as Game;
        const game = { ...gameData, id: snap.id };
        
        if (game.is_deleted || game.status === 'archived') {
          setNotFound(true);
        } else {
          setNotFound(false);
        }
        setSpecificGame(game);
      } else {
        setNotFound(true);
        setSpecificGame(null);
      }
      setSpecificLoading(false);
    }, (err) => {
      console.error('Error fetching specific game:', err);
      setNotFound(true);
      setSpecificLoading(false);
    });

    return () => unsub();
  }, [gameId, trackedGameId]);

  const isLoading = (gameId || trackedGameId) ? specificLoading : activeLoading;
  const resolvedGame = (gameId || trackedGameId) ? specificGame : activeGame;

  useEffect(() => {
    if (!resolvedGame || resolvedGame.is_deleted || resolvedGame.status === 'archived') return;
    if (
      location.pathname.startsWith('/game')
      && ['chip_entry', 'settling', 'settled'].includes(resolvedGame.status)
    ) {
      navigate(`/settlement/${resolvedGame.id}`, { replace: true });
    }
  }, [location.pathname, navigate, resolvedGame?.id, resolvedGame?.is_deleted, resolvedGame?.status]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <LoadingSpinner message="Loading Game..." />
      </div>
    );
  }

  if (notFound || !resolvedGame || resolvedGame.status === 'archived' || resolvedGame.is_deleted) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-6">
        <EmptyState 
          icon="🚫" 
          title="Game Not Found" 
          description="The game you are looking for has been deleted, archived, or doesn't exist."
          action={
            <Button onClick={() => navigate('/setup')} className="mt-4">
              Return to Setup
            </Button>
          }
        />
      </div>
    );
  }

  // Live Game UI
  if (resolvedGame.status === 'active') {
    return <ActiveGamePage game={resolvedGame} />;
  }
  
  // Settlement UI
  if (resolvedGame.status === 'chip_entry' || resolvedGame.status === 'settling' || resolvedGame.status === 'settled') {
    return <SettlementPage game={resolvedGame} />;
  }
  
  // Fallback
  return (
    <div className="flex items-center justify-center min-h-dvh p-6">
      <EmptyState 
        icon="❓" 
        title="Unexpected Game State" 
        description={`The game is in an unknown state: ${resolvedGame.status}`}
        action={
          <Button onClick={() => navigate('/setup')} className="mt-4">
            Return to Setup
          </Button>
        }
      />
    </div>
  );
}

